/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { validateFeatureAccess } from "@/lib/business-logic/settings/feature-access";
import {
  FEATURE_TIERS,
  getTierFeatures,
  type FeatureTier,
} from "@/lib/constants/features/flags";
import {
  type SubscriptionPlan,
  SUBSCRIPTION_PLANS,
} from "@/lib/constants/subscriptions";
import { useSubscription } from "@/hooks/integrations/use-subscription";
import { useMemo } from "react";

const TIER_ORDER: FeatureTier[] = [
  FEATURE_TIERS.MOBILE,
  FEATURE_TIERS.STARTER,
  FEATURE_TIERS.PROFESSIONAL,
  FEATURE_TIERS.BUSINESS,
  FEATURE_TIERS.ENTERPRISE,
];

const TIER_TO_PLAN: Record<FeatureTier, SubscriptionPlan> = {
  [FEATURE_TIERS.MOBILE]: SUBSCRIPTION_PLANS.MOBILE,
  [FEATURE_TIERS.STARTER]: SUBSCRIPTION_PLANS.STARTER,
  [FEATURE_TIERS.PROFESSIONAL]: SUBSCRIPTION_PLANS.PROFESSIONAL,
  [FEATURE_TIERS.BUSINESS]: SUBSCRIPTION_PLANS.BUSINESS,
  [FEATURE_TIERS.ENTERPRISE]: SUBSCRIPTION_PLANS.BUSINESS, // No enterprise plan yet
};

export interface FeatureAccessResult {
  allowed: boolean;
  requiredPlan: SubscriptionPlan | null;
  currentPlan: SubscriptionPlan;
  isLoading: boolean;
}

export function useFeatureAccess(featureFlag: string): FeatureAccessResult {
  const { getCurrentPlan, isLoading } = useSubscription();
  const currentPlan = getCurrentPlan();

  const result = useMemo(() => {
    const access = validateFeatureAccess(featureFlag, currentPlan, true);

    let requiredPlan: SubscriptionPlan | null = null;
    if (!access.allowed) {
      // Find the minimum tier that includes this feature
      for (const tier of TIER_ORDER) {
        if (getTierFeatures(tier).includes(featureFlag)) {
          requiredPlan = TIER_TO_PLAN[tier];
          break;
        }
      }
    }

    return { allowed: access.allowed, requiredPlan };
  }, [featureFlag, currentPlan]);

  return {
    ...result,
    currentPlan,
    isLoading,
  };
}
